import { Stack, Typography, Box } from "@mui/material";
import { Skeleton } from "@mui/material";
import PerformanceItemCard from "./PerformanceItemCard";
import {
  spacing,
  colors,
  typography,
} from "../../../../../../../../assets/styles/tokens";
import {
  formatValue,
  getCurrencySymbolFromCode,
  OutputMetaField,
} from "../../../../../helpers/bizDevelopmentDashboardHelpers";

interface PerformanceItemProps {
  queryOutput: Record<string, any>[];
  comparisonQueryOutput: Record<string, any>[];
  isLoading: boolean;
  isComparisonLoading: boolean;
  isMtd: boolean;
  isComparisonActive: boolean;
  outputMetadata: Record<string, OutputMetaField>;
}

const fmtOrgCurrency = (value: any, dealCurrency: any): string => {
  const symbol = getCurrencySymbolFromCode(dealCurrency);
  const rounded =
    symbol != null && !isNaN(Number(value)) ? Math.round(Number(value)) : value;
  const str = formatValue(rounded);
  if (str === "N/A") return str;
  return symbol ? `${symbol}${str}` : str;
};

const fmtPerc = (raw: any): string => {
  if (raw === null || raw === undefined) return "N/A";
  const n = parseFloat(String(raw));
  if (isNaN(n)) return "N/A";
  return `${(n * 100).toFixed(1)}%`;
};

const PerformanceItem = ({
  queryOutput,
  comparisonQueryOutput,
  isLoading,
  isComparisonLoading,
  isMtd,
  isComparisonActive,
  outputMetadata,
}: PerformanceItemProps) => {
  if (isLoading) {
    return (
      <Stack spacing={2} sx={{ mt: spacing.Spacing.xl.$value }}>
        {[0, 1, 2].map((i) => (
          <Skeleton
            key={i}
            variant="rounded"
            height={320}
            sx={{ borderRadius: spacing.Radius.lg.$value }}
          />
        ))}
      </Stack>
    );
  }

  if (queryOutput.length === 0) {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "50vh",
        }}
      >
        <Typography
          sx={{
            color: colors.GrayLightMode[500].$value,
            fontSize: typography.Size.Text.md.$value,
          }}
        >
          No data found
        </Typography>
      </Box>
    );
  }

  return (
    <Stack spacing={2}>
      {queryOutput.map((row: Record<string, any>, index: number) => {
        const compRow = isComparisonActive
          ? comparisonQueryOutput.find((r) => r.brand_id === row.brand_id)
          : undefined;

        return (
          <PerformanceItemCard
            key={index}
            brand={String(row.brand ?? "")}
            website_id={String(row.brand_id ?? "")}
            totalIncome={fmtOrgCurrency(
              row.totalincome_org_currency,
              row.deal_currency,
            )}
            cpa={fmtOrgCurrency(row.cpa_income_org_currency, row.deal_currency)}
            rs={fmtOrgCurrency(
              row.revshare_income_org_currency,
              row.deal_currency,
            )}
            reg={formatValue(row.reg)}
            clicks={formatValue(row.clicks)}
            ftd={formatValue(row.ftd)}
            ftd_percentage={fmtPerc(row.ftd_perc)}
            qftd={formatValue(row.qftd)}
            qftd_percentage={fmtPerc(row.qftd_perc)}
            qftd_incent={formatValue(row.qftd_incent)}
            ftd_incent_percentage={fmtPerc(row.perc_incent_tier5)}
            rawRow={row}
            isMtd={isMtd}
            isComparisonActive={isComparisonActive}
            isComparisonLoading={isComparisonLoading}
            comparisonRow={compRow}
            outputMetadata={outputMetadata}
          />
        );
      })}
    </Stack>
  );
};

export default PerformanceItem;
